import { useState, useRef, useEffect } from "react";
import { Outlet } from "react-router-dom";
import SideBar from "../sidebar/SideBar";
import Header from "../header/Header";
import { requestSwitchClinic } from "../../../api/auth/user";
import { showErrorToast } from "../../../utils/toaster";
import AccountStatusPrompt from "../../ui/Settings/AccountStatus/AccountStatusPrompt";
import SubscriptionLockScreen from "../../ui/Settings/AccountStatus/SubscriptionLockScreen";
import GraceBanner from "../../ui/Settings/AccountStatus/GraceBanner";
import { useSelector } from "react-redux";

const BasicLayout = ({ children, protectedRouteRef }) => {
  const restricted = useSelector((state) => !!state.auth?.restricted);
  const [collapse, setCollapse] = useState(true);
  const [mobileSidebarOpen, setMobileSideBarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);

  /* Collapse function */
  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  const handleMobSideBar = () => {
    setMobileSideBarOpen(!mobileSidebarOpen);
  };

  // Handle clicks outside the sidebar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileSidebarOpen &&
        overlayRef.current &&
        overlayRef.current === event.target
      ) {
        setMobileSideBarOpen(false);
      }
    };

    // Add event listener when sidebar is open
    if (mobileSidebarOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [mobileSidebarOpen]);

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && mobileSidebarOpen) {
        setMobileSideBarOpen(false);
      }
    };

    if (mobileSidebarOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [mobileSidebarOpen]);

  const [loadingSwitchCLinic, setLoadingSwitchClinic] = useState(false);

  /**
   * Switch the user's active clinic.
   *
   * Audit fix: previously the new token was written to localStorage,
   * then `refreshAuth()` was kicked off in fire-and-forget mode.
   * Between those two steps any in-flight component reading the auth
   * Redux slice still sees the OLD clinic name / role / variant /
   * linked_clinics — while the very next API call goes to the NEW
   * tenant. That race was a real cross-tenant UX leak.
   *
   * Now we:
   *   1. AWAIT `refreshAuth()` so the Redux user state is fully
   *      reconciled before we lower the loading flag.
   *   2. Show an error toast (and roll back) if the refresh fails,
   *      so the UI never claims success on a half-failed switch.
   */
  const handleSwitchClinic = async (id) => {
    setLoadingSwitchClinic(true);
    const previousToken = localStorage.getItem("token");
    try {
      const payload = { clinic_id: id };
      const response = await requestSwitchClinic(payload);
      const token = response.data.data.token;
      localStorage.setItem("token", token);

      const refreshFn = protectedRouteRef.current?.refreshAuth;
      if (typeof refreshFn === "function") {
        await Promise.resolve(refreshFn());
      }
    } catch (error) {
      // Roll back the localStorage swap — `refreshAuth` may have
      // already failed, leaving the user authenticated as the new
      // tenant in Redux but with no profile loaded. Restoring the
      // previous token keeps subsequent retries on the right
      // tenant.
      if (previousToken) localStorage.setItem("token", previousToken);
      showErrorToast(error);
    } finally {
      setLoadingSwitchClinic(false);
    }
  };

  // Free trial over and no plan bought: nothing but the buy-a-plan screen.
  if (restricted) return <SubscriptionLockScreen />;

  return (
    <div className="">
      {/* Login-time notice: subscription due/expired, credits low/over. */}
      <AccountStatusPrompt />
      {/* Full-screen overlay while a clinic-switch is in flight. The
          switch is asynchronous — token swap + refreshAuth — and a
          mid-switch click on a sidebar link could fire an API call
          against an inconsistent tenant. Blocking interaction during
          the switch keeps state coherent. */}
      {loadingSwitchCLinic && (
        <div
          className="fixed inset-0 z-[10000] bg-black/30 flex items-center justify-center"
          role="status"
          aria-live="polite"
          aria-label="Switching clinic"
        >
          <div className="bg-white rounded-lg shadow-card px-6 py-4 flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
            <span className="text-sm text-darkgrey">Switching clinic…</span>
          </div>
        </div>
      )}
      {/* <!-- ===== Page Wrapper Start ===== --> */}
      <div className="flex h-screen overflow-x-hidden scrollbar-hidden">
        {/* <!-- ===== Sidebar Start ===== --> */}

        {/* Mobile View with smooth transition */}
        <div
          ref={overlayRef}
          className={`fixed z-50 overflow-y-auto topindex scrollbar-hidden h-full duration-300 ease-in-out bg-darkgrey bg-opacity-30 w-full md:hidden transition-transform
          ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          <div
            ref={sidebarRef}
            className=" h-screen w-auto max-w-[1px]  transition-transform duration-300 ease-in-out"
          >
            <SideBar handleSwitchClinic={handleSwitchClinic} collapse={false} />
          </div>
        </div>
        {/* Mobile View End */}

        {/* Desktop view */}
        <div className="hidden md:block">
          <SideBar
            collapse={collapse}
            handleSwitchClinic={handleSwitchClinic}
          />
        </div>
        <div className="relative hidden md:block">
          {/* <!-- ===== Collapse Button ===== --> */}
          <button
            onClick={() => handleCollapse()}
            aria-controls="sidebar"
            style={{ zIndex: 999 }}
            className="absolute md:-right-4 md:top-3 lg:bottom-20 w-[43px] h-[44px] rounded-full bg-[#0285c7] flex justify-center items-center"
          >
            <div className={`${collapse ? "rotate-180" : ""} duration-300`}>
              <img src="/icons/arrow.svg" className="" alt="" />
            </div>
          </button>
        </div>
        {/* Desktop view End*/}
        {/* <!-- ===== Sidebar End ===== --> */}

        {/* <!-- ===== Content Area Start ===== --> */}
        <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden main-content">
          {/* <!-- ===== Header Start ===== --> */}
          <Header handleMobSideBar={handleMobSideBar} />
          {/* <!-- ===== Header End ===== --> */}
          {/* Grace period after a plan lapses: a standing warning. */}
          <GraceBanner />

          {/* <!-- ===== Main Content Start ===== --> */}
          <main>
            <div className="mx-auto max-w-screen-2xl p-4 md:p-6 2xl:pt-6 h-[90vh] mb-10 lg:mb-0">
              <Outlet />
              {children}
            </div>
          </main>
          {/* <!-- ===== Main Content End ===== --> */}
        </div>
        {/* <!-- ===== Content Area End ===== --> */}
      </div>
      {/* <!-- ===== Page Wrapper End ===== --> */}
    </div>
  );
};

export default BasicLayout;
